<template>
  <!-- Offline State -->
  <div v-if="!isOnline" class="sm:py-12">
    <div class="sm:px-6 lg:px-8">
      <div class="flex flex-col justify-center items-center text-center sm:py-12">
        <div class="mb-6 text-6xl text-gray-400 dark:text-gray-600">
          <i class="pi pi-wifi"></i>
        </div>
        <h2 class="mb-2 text-2xl font-bold text-gray-700 dark:text-gray-300">You're offline</h2>
        <p class="mb-6 text-gray-600 dark:text-gray-400">Please check your internet connection to access your driver
          dashboard.</p>
        <button
          @click="checkConnection"
          class="px-4 py-2 text-white bg-green-600 rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2">
          Try Again
        </button>
      </div>
    </div>
  </div>

  <!-- Online State -->
  <div v-else class="sm:py-12">
    <div class="sm:px-6 lg:px-8">
      <!-- Header and Toggle Availability -->
      <div class="flex flex-col justify-between items-start mb-6 md:flex-row md:items-center">
        <h1 class="my-4 text-2xl font-bold text-gray-900 dark:text-white md:mb-0">
          Welcome {{ user?.name }}
        </h1>
        <Button
          :label="driverStore.stats.isAvailable ? 'Go Offline' : 'Go Online'"
          :class="
            driverStore.stats.isAvailable
              ? 'border-red-800 text-red-800 dark:border-red-600 dark:text-red-600'
              : ' border-green-800 text-green-800 dark:border-green-600 dark:text-green-600'
          "
          :loading="driverStore.loading"
          @click="toggleAvailability"
          icon="pi pi-power-off" />
      </div>

      <!-- Loading Indicator -->
      <div v-if="driverStore.loading" class="flex justify-center my-8">
        <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" />
      </div>

      <!-- Error Message -->
      <Message v-if="driverStore.error" severity="error" :closable="false" class="mb-4">
        {{ driverStore.error }}
      </Message>

      <!-- Stats Cards -->
      <DateFilter @dateFilter="handleDateFilter" />
      <div v-if="!driverStore.loading" class="grid grid-cols-1 gap-6 mb-8 md:grid-cols-4">
        <div class="overflow-hidden p-6 bg-white rounded-lg shadow-sm dark:bg-gray-800">
          <h3 class="text-lg font-medium text-gray-900 dark:text-white">Total Orders</h3>
          <p class="mt-2 text-3xl font-bold text-emerald-600 dark:text-emerald-400">
            {{ driverStore.stats.totalOrders }}
          </p>
        </div>
        <div class="overflow-hidden p-6 bg-white rounded-lg shadow-sm dark:bg-gray-800">
          <h3 class="text-lg font-medium text-gray-900 dark:text-white">Completed Orders</h3>
          <p class="mt-2 text-3xl font-bold text-emerald-600 dark:text-emerald-400">
            {{ driverStore.stats.completedOrders }}
          </p>
        </div>
        <!-- <div class="overflow-hidden p-6 bg-white rounded-lg shadow-sm dark:bg-gray-800">
          <h3 class="text-lg font-medium text-gray-900 dark:text-white">Assigned Orders</h3>
          <p class="mt-2 text-3xl font-bold text-blue-600 dark:text-blue-400">{{ driverStore.stats.assignedOrders }}</p>
        </div> -->
        <!-- <div class="overflow-hidden p-6 bg-white rounded-lg shadow-sm dark:bg-gray-800">
          <h3 class="text-lg font-medium text-gray-900 dark:text-white">Total Earnings</h3>
          <p class="mt-2 text-3xl font-bold text-green-600 dark:text-green-400">{{ formatCurrency(driverStore.stats.earnings) }}</p>
        </div> -->
      </div>

      <!-- Current Orders -->
      <div class="mb-8">
        <h2 class="mb-4 text-xl font-semibold text-gray-800 dark:text-white">Current Orders</h2>

        <div v-if="driverStore.loading" class="flex justify-center my-4">
          <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="3" />
        </div>

        <Message v-else-if="driverStore.error" severity="error" :closable="false">
          {{ driverStore.error }}
        </Message>

        <Message
          v-else-if="driverStore.recentOrders.length === 0"
          severity="info"
          :closable="false">
          No current orders assigned to you.
        </Message>

        <div v-else class="overflow-hidden bg-white rounded-lg shadow-sm dark:bg-gray-800">
          <DataTable
            :items="orders"
            :columns="columns"
            :pagination="pagination"
            @page-change="handlePageChange"
            @route-change="handleRouteChange">
            <template #business="{ item }">
              <div class="text-sm font-medium text-gray-900 dark:text-gray-200">
                {{ item?.business?.name || 'N/A' }}
              </div>
              <div class="text-sm text-gray-500 dark:text-gray-400">
                {{ item?.business?.address || 'N/A' }}
              </div>
            </template>
            <template #customer="{ item }">
              <div class="text-sm font-medium text-gray-900 dark:text-gray-200">
                {{ item?.customer?.name || 'N/A' }}
              </div>
              <div class="text-sm text-gray-500 dark:text-gray-400">
                {{ item?.customer?.phone || 'N/A' }}
              </div>
            </template>

            <template #total="{ item }">
              {{ nairaSymbol }}{{ Number(item?.total_amount || 0).toFixed() }}
            </template>

            <template #order_status="{ item }">
              <span
                class="inline-flex px-2 text-xs font-semibold leading-5 rounded-full"
                :class="statusColors[item?.status || 'pending']">
                {{ orderStatusLabels[item?.status || 'pending'] }}
              </span>
            </template>

            <template #payment_status="{ item }">
              <span
                class="inline-flex px-2 text-xs font-semibold leading-5 rounded-full"
                :class="statusColors[item?.payment_status || 'pending']">
                {{ paymentStatusLabels[item?.payment_status || 'pending'] }}
              </span>
            </template>

            <template #date="{ item }">
              {{ item?.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A' }}
            </template>

            <!-- <template #actions="{ item }">
              <select>
                <option value="pending">Pending</option>
              </select>
            </template> -->
          </DataTable>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useRouter } from 'vue-router'
import { useDriverStore } from '@/stores/driverStore'
import { formatCurrency } from '@/utils/format'
import { storeToRefs } from 'pinia'
import DataTable from '@/components/general/DataTable.vue'
import DateFilter from '@/components/general/DateFilter.vue'

// Components
import Button from 'primevue/button'
import ProgressSpinner from 'primevue/progressspinner'
import Message from 'primevue/message'
import Badge from 'primevue/badge'
import Column from 'primevue/column'
import { statusColors, columns, paymentStatusLabels, orderStatusLabels } from '@/constants'
import { nairaSymbol } from '@/constants'
import { useAuthStore } from '@/stores/authStore'

const authStore = useAuthStore()
const { user } = storeToRefs(authStore)
const driverStore = useDriverStore()
const toast = useToast()
const router = useRouter()
const isOnline = ref(navigator.onLine)

const { recentOrders: orders, ordersPagination: pagination } = storeToRefs(driverStore)

// Update online status when connectivity changes
const updateOnlineStatus = () => {
  isOnline.value = navigator.onLine
}

// Function to manually check connection and refresh data if online
const checkConnection = async () => {
  updateOnlineStatus()
  if (isOnline.value) {
    await driverStore.initialize()
  }
}

// Initialize data
onMounted(() => {
  // Add event listeners for online/offline events
  window.addEventListener('online', updateOnlineStatus)
  window.addEventListener('offline', updateOnlineStatus)

  // Only fetch data if online
  if (isOnline.value) {
    driverStore.initialize()
  }
})

onUnmounted(() => {
  // Clean up event listeners
  window.removeEventListener('online', updateOnlineStatus)
  window.removeEventListener('offline', updateOnlineStatus)
})

const handlePageChange = (page) => {
  driverStore.fetchRecentOrders(page)
}

const handleRouteChange = (id) => {
  router.push({ name: ROUTES.DASHBOARD_ORDER, params: { id: id } })
}

const updateStatus = async (orderId, status) => {
  try {
    await driverStore.updateOrderStatus(orderId, status)
    toast.add({
      severity: 'success',
      summary: 'Order Updated',
      detail: `Order status changed to ${status.replace('_', ' ')}`,
      life: 3000,
    })
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Update Failed',
      detail: error.message || 'Failed to update order status',
      life: 3000,
    })
  }
}

// Format date
const formatDate = (dateString) => {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

const handleDateFilter = (dateFilter, startDate, endDate) => {
  driverStore.fetchDashboardData(dateFilter, startDate, endDate)
}

// Toggle driver availability
const toggleAvailability = async () => {
  try {
    const newStatus = await driverStore.toggleAvailability()
    toast.add({
      severity: 'success',
      summary: 'Status Updated',
      detail: `You are now ${newStatus ? 'online' : 'offline'}`,
      life: 3000,
    })
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Update Failed',
      detail: error.message || 'Failed to update availability status',
      life: 3000,
    })
  }
}

// View order details
const viewOrderDetails = (orderId) => {
  router.push({ name: 'driver.orders.show', params: { id: orderId } })
}

// Get status severity for PrimeVue Badge
const getStatusSeverity = (status) => {
  const severities = {
    pending: 'warning',
    processing: 'info',
    assigned: 'info',
    picked_up: 'warning',
    out_for_delivery: 'warning',
    delivered: 'success',
    cancelled: 'danger',
  }
  return severities[status] || 'secondary'
}
</script>
